// Filtros rápidos de la lista de cargas (oct 2026): pastillas bajo la barra de
// fecha para quedarse con las cargas de un controlador, una fábrica, un
// transportista o solo las pendientes.
//
// Reglas:
//  - Una dimensión solo se ofrece si en el día hay AL MENOS DOS valores
//    distintos: con uno solo, filtrar no cambia nada. Si ya tiene un filtro
//    puesto se sigue mostrando aunque no cumpla (para poder verlo y quitarlo,
//    p. ej. tras cambiar de día).
//  - Dentro de una dimensión se pueden marcar varios valores (O); entre
//    dimensiones se suman (Y).
//  - Los recuentos de cada opción ya tienen aplicados los OTROS filtros: así
//    nunca se elige una combinación que deje la lista vacía sin avisar.
//  - Los valores se agrupan sin distinguir mayúsculas ni espacios de más
//    ("STRAW M.B." y "Straw M.B. " son la misma fábrica).
//  - El controlador es solo para admins (features.filterByController) y, además,
//    solo con cargas que ya lo enseñan en la tarjeta. Fábrica y transportista
//    son para todos: a un controlador con dos fábricas o dos transportistas en
//    el día también le salen.

import type { Position } from '../../types';
import { supplierBadgeOf } from '../../services/selfManaged';

export type QuickFilterDim = 'controller' | 'factory' | 'carrier';
export const QUICK_FILTER_DIMS: readonly QuickFilterDim[] = ['controller', 'factory', 'carrier'];

export interface QuickFilters {
  controller: string[];
  factory: string[];
  carrier: string[];
  pending: boolean;
}

export const EMPTY_QUICK_FILTERS: QuickFilters = { controller: [], factory: [], carrier: [], pending: false };

// Clave de "sin dato" (carga sin transportista, sin controlador…). La etiqueta
// la pone la UI traducida.
export const NONE_KEY = '__none__';

export interface FacetOption {
  key: string;
  label: string;
  count: number; // cargas que quedarían con esta opción, con los OTROS filtros aplicados
}

export interface Facet {
  dim: QuickFilterDim;
  options: FacetOption[];
  visible: boolean;
}

export interface QuickFacets {
  controller: Facet;
  factory: Facet;
  carrier: Facet;
  pending: { pendingCount: number; visible: boolean };
}

/**
 * Correo del controlador TAL Y COMO LO VE LA TARJETA: solo si el servidor marcó
 * la carga como vista por un admin (el "(correo)" al final de factoryAdress).
 * Mismo criterio que PositionCard, para que el filtro no enseñe más que ella.
 */
export function visibleControllerOf(pos: Position): string | undefined {
  const factoryAddress = (pos.factoryAdress ?? pos.factoryAddress) as string | undefined;
  const factoryMatch = factoryAddress?.match(/^(.*?)\s*\(\s*(.*?)\s*\)\s*$/);
  if (!factoryMatch) return undefined;
  return (pos.controllerName as string | undefined)?.trim() || factoryMatch[2].trim() || undefined;
}

export function normalizeKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

function rawValueOf(dim: QuickFilterDim, pos: Position): string | undefined {
  switch (dim) {
    case 'controller': return visibleControllerOf(pos);
    case 'factory': return supplierBadgeOf(pos);
    case 'carrier': return (pos.carrierName ?? '').toString().trim() || undefined;
  }
}

// Una carga "pendiente" aún no tiene cargados todos sus contenedores. Sin
// número previsto (self-managed recién creada) cuenta como pendiente mientras
// no tenga ninguno cargado.
export function isPendingCargo(pos: Position): boolean {
  const planned = Number(pos.totalContainers) || 0;
  const loaded = Number(pos.totalContainerLoaded) || 0;
  if (planned === 0) return loaded === 0;
  return loaded < planned;
}

export interface QuickFilterOptions {
  /** features.filterByController: solo admins. */
  allowController: boolean;
}

// ¿Aplica la dimensión a este día? El controlador solo para admins y cuando
// alguna carga lo trae visible; fábrica y transportista siempre.
function dimApplies(dim: QuickFilterDim, positions: Position[], opts: QuickFilterOptions): boolean {
  if (dim !== 'controller') return true;
  return opts.allowController && positions.some((p) => visibleControllerOf(p) !== undefined);
}

function keyOf(dim: QuickFilterDim, pos: Position): string {
  const raw = rawValueOf(dim, pos);
  return raw ? normalizeKey(raw) : NONE_KEY;
}

function matchesDim(dim: QuickFilterDim, pos: Position, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return selected.includes(keyOf(dim, pos));
}

function matchesAll(pos: Position, f: QuickFilters, skip?: QuickFilterDim | 'pending'): boolean {
  for (const dim of QUICK_FILTER_DIMS) {
    if (dim !== skip && !matchesDim(dim, pos, f[dim])) return false;
  }
  if (skip !== 'pending' && f.pending && !isPendingCargo(pos)) return false;
  return true;
}

export function hasActiveQuickFilters(f: QuickFilters): boolean {
  return f.pending || QUICK_FILTER_DIMS.some((d) => f[d].length > 0);
}

// Lo que de verdad se aplica: sin permiso de admin, un filtro de controlador
// guardado (otra sesión en el mismo navegador) se ignora.
export function effectiveQuickFilters(f: QuickFilters, opts: QuickFilterOptions): QuickFilters {
  return opts.allowController || f.controller.length === 0 ? f : { ...f, controller: [] };
}

export function applyQuickFilters(positions: Position[], f: QuickFilters): Position[] {
  if (!hasActiveQuickFilters(f)) return positions;
  return positions.filter((p) => matchesAll(p, f));
}

export function buildQuickFacets(positions: Position[], f: QuickFilters, opts: QuickFilterOptions): QuickFacets {
  const facet = (dim: QuickFilterDim): Facet => {
    if (!dimApplies(dim, positions, opts)) {
      return { dim, options: [], visible: false };
    }
    // Etiqueta = la forma más repetida de cada valor; recuento = con los demás
    // filtros puestos.
    const forms = new Map<string, Map<string, number>>();
    for (const p of positions) {
      const key = keyOf(dim, p);
      const raw = rawValueOf(dim, p) ?? '';
      const byForm = forms.get(key) ?? new Map<string, number>();
      byForm.set(raw, (byForm.get(raw) ?? 0) + 1);
      forms.set(key, byForm);
    }
    const counts = new Map<string, number>();
    for (const p of positions) {
      if (!matchesAll(p, f, dim)) continue;
      const key = keyOf(dim, p);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const options: FacetOption[] = [...forms.entries()].map(([key, byForm]) => {
      const label = [...byForm.entries()].sort((a, b) => b[1] - a[1])[0][0];
      return { key, label, count: counts.get(key) ?? 0 };
    });
    // Seleccionadas que hoy no existen (vienen de otro día): se listan igual,
    // a 0, para que se puedan desmarcar.
    for (const key of f[dim]) {
      if (!forms.has(key)) options.push({ key, label: key, count: 0 });
    }
    options.sort((a, b) =>
      (a.key === NONE_KEY ? 1 : 0) - (b.key === NONE_KEY ? 1 : 0)
      || b.count - a.count
      || a.label.localeCompare(b.label));
    const distinct = forms.size;
    return { dim, options, visible: distinct >= 2 || f[dim].length > 0 };
  };

  const base = positions.filter((p) => matchesAll(p, f, 'pending'));
  const pendingCount = base.filter(isPendingCargo).length;
  const anyPending = positions.some(isPendingCargo);
  const anyDone = positions.some((p) => !isPendingCargo(p));

  return {
    controller: facet('controller'),
    factory: facet('factory'),
    carrier: facet('carrier'),
    pending: { pendingCount, visible: (anyPending && anyDone) || f.pending },
  };
}

// ── Persistencia (sessionStorage) ──────────────────────────────────────────
// Sobreviven a abrir una carga y volver, y al cambiar de día (seguir a un
// controlador día a día). Se olvidan al cerrar la app.
const STORAGE_KEY = 'POSITION_QUICK_FILTERS';

export function loadQuickFilters(): QuickFilters {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_QUICK_FILTERS;
    const v = JSON.parse(raw) as Partial<QuickFilters>;
    const list = (x: unknown) => (Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : []);
    return {
      controller: list(v.controller),
      factory: list(v.factory),
      carrier: list(v.carrier),
      pending: v.pending === true,
    };
  } catch {
    return EMPTY_QUICK_FILTERS;
  }
}

export function saveQuickFilters(f: QuickFilters): void {
  try {
    if (hasActiveQuickFilters(f)) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(f));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* sin sessionStorage: los filtros duran lo que la pantalla */ }
}
